"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from '@/lib/api';
import { useAuth } from '../context/AuthContext';
import { Search, MoreHorizontal, Copy, Trash2, Plus, Key, Loader2, Check, ClipboardCheck } from "lucide-react";

interface ApiToken {
  id: number;
  name: string;
  permissions: string[];
  expiresAt: string | null;
  lastUsed: string | null;
  createdAt: string;
}

const allPermissions = ["read", "write", "devices:read", "devices:write", "groups:read", "groups:write", "policies:read", "policies:write", "logs:read", "admin"];

export function ApiTokensPage() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", permissions: [] as string[], expiresInDays: "" });
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const { data: tokens, isLoading } = useQuery({
    queryKey: ["api-tokens"],
    queryFn: () => api.getApiTokens(token!),
    enabled: !!token,
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; permissions: string[]; expiresInDays?: number }) => api.createApiToken(data, token!),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["api-tokens"] });
      setCreateDialogOpen(false);
      setFormData({ name: "", permissions: [], expiresInDays: "" });
      setCopiedToken(result.token);
      setTimeout(() => setCopiedToken(null), 5000);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deleteApiToken(id, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-tokens"] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      name: formData.name,
      permissions: formData.permissions,
      expiresInDays: formData.expiresInDays ? parseInt(formData.expiresInDays) : undefined,
    });
  };

  const handlePermissionChange = (perm: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, perm]
        : prev.permissions.filter(p => p !== perm),
    }));
  };

  if (isLoading) {
    return <div className="animate-pulse space-y-4"><Card><CardContent className="pt-6"><div className="h-4 w-3/4 animate-pulse rounded bg-muted" /></CardContent></Card></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">API Tokens</h1>
          <p className="text-muted-foreground">Manage API access tokens for external integrations</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" />Create Token</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create API Token</DialogTitle><DialogDescription>Generate a new API token with specific permissions</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid gap-4 py-4">
                <div className="space-y-2"><Label htmlFor="name">Token Name</Label><Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="e.g. Monitoring Integration" /></div>
                <div className="space-y-2"><Label htmlFor="expiresInDays">Expires In (days, optional)</Label><Input id="expiresInDays" type="number" value={formData.expiresInDays} onChange={(e) => setFormData({ ...formData, expiresInDays: e.target.value })} placeholder="Leave empty for no expiry" /></div>
                <div className="space-y-2"><Label>Permissions</Label>
                  <div className="flex flex-wrap gap-2">
                    {allPermissions.map(perm => (
                      <label key={perm} className="flex items-center gap-2 px-3 py-1 rounded border cursor-pointer hover:bg-accent">
                        <input type="checkbox" checked={formData.permissions.includes(perm)} onChange={(e) => handlePermissionChange(perm, e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                        <span className="text-sm">{perm}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setCreateDialogOpen(false); setFormData({ name: "", permissions: [], expiresInDays: "" }); }}>Cancel</Button>
                <Button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : "Create Token"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {copiedToken && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <Check className="h-5 w-5" />
          <span>Token copied to clipboard!</span>
          <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard.writeText(copiedToken); setCopiedToken(null); }}>
            <ClipboardCheck className="h-4 w-4" />
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle>API Tokens</CardTitle>
            <Input placeholder="Search tokens..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Last Used</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens?.filter((t: any) => t.name.toLowerCase().includes(search.toLowerCase())).map((tkn: any) => (
                <TableRow key={tkn.id}>
                  <TableCell className="font-medium">{tkn.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {tkn.permissions.map((p: string) => <Badge key={p} variant="secondary" className="text-xs">{p}</Badge>)}
                    </div>
                  </TableCell>
                  <TableCell>{tkn.expiresAt ? new Date(tkn.expiresAt).toLocaleDateString() : "Never"}</TableCell>
                  <TableCell>{tkn.lastUsed ? new Date(tkn.lastUsed).toLocaleString() : "Never"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{new Date(tkn.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(tkn.name); setCopiedToken(tkn.name); }}>Copy Name</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => deleteMutation.mutate(tkn.id)} className="text-destructive focus:text-destructive"><Trash2 className="mr-2 h-4 w-4" />Revoke Token</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {tokens?.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No API tokens created yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}